import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Search, RefreshCw, ChevronDown, ChevronRight, ClipboardList, X } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (s) => {
  if (!s) return '—';
  try {
    return new Date(s).toLocaleString('en-ZA', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    });
  } catch {
    return s;
  }
};

const actionColor = (a = '') => {
  const s = a.toLowerCase();
  if (s.includes('login_success')) return 'border-green-500 text-green-700';
  if (s.includes('login_failed')) return 'border-red-500 text-red-700';
  if (s.includes('delete') || s.includes('purge') || s.includes('unsplit')) return 'border-red-500 text-red-700';
  if (s.includes('create') || s.includes('split') || s.includes('import') || s.includes('allocate')) return 'border-blue-500 text-blue-700';
  if (s.includes('update') || s.includes('reassign') || s.includes('match')) return 'border-amber-500 text-amber-700';
  return 'border-slate-400 text-slate-700';
};

const AdminAuditTrailPage = () => {
  const { token } = useAuth();
  const headers = React.useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [q, setQ] = useState('');
  const [action, setAction] = useState('');
  const [actorId, setActorId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(100);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [actions, setActions] = useState([]);
  const [actors, setActors] = useState([]);

  const loadFacets = useCallback(async () => {
    try {
      const [a, u] = await Promise.all([
        axios.get(`${API_URL}/api/admin/audit-logs/distinct-actions`, { headers }),
        axios.get(`${API_URL}/api/admin/audit-logs/distinct-actors`, { headers }),
      ]);
      setActions(a.data?.actions || []);
      setActors(u.data?.actors || []);
    } catch (err) {
      // non-fatal
    }
  }, [headers]);

  const search = useCallback(async (nextPage = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (action) params.set('action', action);
      if (actorId) params.set('actor_id', actorId);
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      params.set('page', String(nextPage));
      params.set('page_size', String(pageSize));
      const r = await axios.get(`${API_URL}/api/admin/audit-logs/search?${params.toString()}`, { headers });
      setRows(r.data?.logs || []);
      setTotal(r.data?.total || 0);
      setTotalPages(r.data?.total_pages || 0);
      setPage(r.data?.page || nextPage);
      setExpanded(new Set());
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    } finally {
      setLoading(false);
    }
  }, [q, action, actorId, fromDate, toDate, pageSize, headers]);

  useEffect(() => {
    loadFacets();
    search(1);
  }, []);

  const clearFilters = () => {
    setQ('');
    setAction('');
    setActorId('');
    setFromDate('');
    setToDate('');
    setPage(1);
    setTimeout(() => search(1), 0);
  };

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-6" data-testid="admin-audit-trail-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center">
          <ClipboardList className="h-5 w-5 text-slate-700" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Audit Trail</h1>
          <p className="text-sm text-slate-500">Every action taken on this portal. Search by name, RP code, client, transaction, action, or actor.</p>
        </div>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Filters</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">Search (name, RP, ID, description, amount…)</label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && search(1)}
                  placeholder="Ricky Hart / 03-02 / R1,436.36 / split"
                  className="pl-8"
                  data-testid="audit-search-input"
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-slate-500">Action</label>
              <Select value={action || 'all'} onValueChange={(v) => setAction(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="audit-action-select"><SelectValue placeholder="Any action" /></SelectTrigger>
                <SelectContent className="max-h-96">
                  <SelectItem value="all">Any action</SelectItem>
                  {actions.map(a => (
                    <SelectItem key={a} value={a}>{a}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Actor (admin)</label>
              <Select value={actorId || 'all'} onValueChange={(v) => setActorId(v === 'all' ? '' : v)}>
                <SelectTrigger data-testid="audit-actor-select"><SelectValue placeholder="Any actor" /></SelectTrigger>
                <SelectContent className="max-h-96">
                  <SelectItem value="all">Any actor</SelectItem>
                  {actors.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}{a.email ? ` (${a.email})` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-slate-500">From</label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="audit-from-date" />
            </div>
            <div>
              <label className="text-xs text-slate-500">To</label>
              <Input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="audit-to-date" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={() => search(1)} disabled={loading} className="gap-1" data-testid="audit-search-btn">
              <Search className="h-4 w-4" /> Search
            </Button>
            <Button variant="outline" onClick={clearFilters} disabled={loading} className="gap-1" data-testid="audit-clear-btn">
              <X className="h-4 w-4" /> Clear
            </Button>
            <Button variant="outline" onClick={() => search(page)} disabled={loading} className="gap-1" data-testid="audit-refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <div className="ml-auto text-xs text-slate-500">
              {loading ? 'Loading…' : `${total.toLocaleString()} record(s) · page ${page} / ${totalPages || 1}`}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"></TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Actor</TableHead>
                  <TableHead>Investor</TableHead>
                  <TableHead>Summary</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 && !loading && (
                  <TableRow><TableCell colSpan={6} className="text-center py-10 text-slate-500">No results.</TableCell></TableRow>
                )}
                {rows.map((r, i) => {
                  const rid = r.id || `${r._display_ts}-${i}`;
                  const isOpen = expanded.has(rid);
                  const summary = buildSummary(r);
                  return (
                    <React.Fragment key={rid}>
                      <TableRow className="cursor-pointer hover:bg-slate-50" onClick={() => toggleExpand(rid)} data-testid={`audit-row-${i}`}>
                        <TableCell className="pr-0">
                          {isOpen ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                        </TableCell>
                        <TableCell className="text-xs font-mono whitespace-nowrap">{formatDateTime(r._display_ts)}</TableCell>
                        <TableCell><Badge variant="outline" className={actionColor(r.action)}>{r.action || '—'}</Badge></TableCell>
                        <TableCell className="text-xs">
                          <div className="font-medium">{r._actor_name || '—'}</div>
                          {r._actor_email && <div className="text-slate-500">{r._actor_email}</div>}
                        </TableCell>
                        <TableCell className="text-xs">
                          <div>{r._investor_name || '—'}</div>
                          {r._investor_id && <div className="text-slate-500 font-mono">{String(r._investor_id).slice(0, 12)}…</div>}
                        </TableCell>
                        <TableCell className="text-xs text-slate-700">{summary}</TableCell>
                      </TableRow>
                      {isOpen && (
                        <TableRow>
                          <TableCell colSpan={6} className="bg-slate-50 p-4">
                            <pre className="text-[11px] whitespace-pre-wrap break-words text-slate-700 font-mono">
                              {JSON.stringify(stripInternal(r), null, 2)}
                            </pre>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t">
              <div className="text-xs text-slate-500">Page {page} of {totalPages} · {total.toLocaleString()} records</div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={loading || page <= 1} onClick={() => search(page - 1)} data-testid="audit-prev-page">Previous</Button>
                <Button variant="outline" size="sm" disabled={loading || page >= totalPages} onClick={() => search(page + 1)} data-testid="audit-next-page">Next</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Strip the enrichment-only fields we added on the backend for display
const stripInternal = (r) => {
  const copy = { ...r };
  delete copy._display_ts;
  delete copy._actor_id;
  delete copy._actor_name;
  delete copy._actor_email;
  delete copy._investor_id;
  delete copy._investor_name;
  return copy;
};

// Build a compact human summary line for the collapsed row
const buildSummary = (r) => {
  const bits = [];
  if (r.parent_amount !== undefined && r.parent_amount !== null) {
    bits.push(`R${Number(r.parent_amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`);
  } else if (r.amount !== undefined && r.amount !== null) {
    bits.push(`R${Number(r.amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`);
  }
  if (r.parent_description) bits.push(String(r.parent_description).slice(0, 80));
  if (r.bank_transaction_id) bits.push(`bank ${String(r.bank_transaction_id).slice(0, 8)}…`);
  if (r.transaction_id) bits.push(`txn ${String(r.transaction_id).slice(0, 8)}…`);
  if (r.child_ids && Array.isArray(r.child_ids)) bits.push(`${r.child_ids.length} child(ren)`);
  if (r.deleted) bits.push(`${r.deleted} deleted`);
  if (r.children_updated) bits.push(`${r.children_updated} updated`);
  if (r.details?.email && bits.length === 0) bits.push(r.details.email);
  return bits.join(' · ') || '—';
};

export default AdminAuditTrailPage;
